import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Response } from '@ui/src';
import { CASP, CASPCurrency, TransactionType } from '../../types';
import { transactionsTypesApi } from '../services/transactions-types';

export interface TransactionsTypesState {
  values?: TransactionType[];
  prevValues?: TransactionType[];
  casp?: CASP[];
  caspItem?: CASP;
}

const initialState: TransactionsTypesState = {
  values: undefined,
  prevValues: undefined,
};

export const transactionsTypesSlice = createSlice({
  name: 'transactionsTypes',
  initialState,
  reducers: {
    onToggleParent: (state, action: PayloadAction<string>) => {
      state.prevValues = state.values;

      const nextTypes = state.values?.map((item) => {
        if (item.id === action.payload) {
          const nextChildren = item?.children?.map((el) => ({
            ...el,
            active: !item.active,
          }));
          return { ...item, active: !item.active, children: nextChildren };
        }
        return item;
      });

      state.values = nextTypes;
    },
    onToggleChildren: (
      state,
      action: PayloadAction<{
        typeId: string;
        subtypeId: string;
        value: boolean;
      }>,
    ) => {
      state.prevValues = state.values;

      const nextTypes = state.values?.map((item) => {
        if (item.id === action.payload.typeId && item.children?.length) {
          const nextChildren = item.children.map((el) => {
            if (el.id === action.payload.subtypeId) {
              return { ...el, active: action.payload.value };
            }
            return el;
          });
          return { ...item, children: nextChildren };
        }
        return item;
      });

      state.values = nextTypes;
    },

    onUndoChange: (state) => {
      state.values = state.prevValues;
    },
    clearCASPSidePanel: (state) => {
      state.caspItem = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        transactionsTypesApi.endpoints.getEmployeesTransactionsTypes
          .matchFulfilled,
        (state, action: PayloadAction<Response<TransactionType>>) => {
          state.values = action.payload.data.edges.map((item) => item.node);
        },
      )
      .addMatcher(
        transactionsTypesApi.endpoints.updateEmployeesTransactionsTypes
          .matchFulfilled,
        (state, action: PayloadAction<{ data: TransactionType }>) => {
          state.values = state.values?.map((item) => {
            const result = { ...item };

            if (action.payload.data.id === result.id) {
              result.allowedTransfers = action.payload.data.allowedTransfers;
              result.active = action.payload.data.active;
            }

            return result;
          });
        },
      )
      .addMatcher(
        transactionsTypesApi.endpoints.getEmployeesCASP.matchFulfilled,
        (state, action: PayloadAction<Response<CASP>>) => {
          state.casp = action.payload.data.edges.map((item) => item.node);
        },
      )
      .addMatcher(
        transactionsTypesApi.endpoints.getCASPItem.matchFulfilled,
        (state, action: PayloadAction<{ data: CASP }>) => {
          state.caspItem = action.payload.data;
        },
      )
      .addMatcher(
        transactionsTypesApi.endpoints.attachCurrenciesToCASP.matchFulfilled,
        (
          state,
          action: PayloadAction<{ data: CASPCurrency[] }> & {
            meta: {
              arg: {
                originalArgs: {
                  caspId: string;
                };
              };
            };
          },
        ) => {
          state.casp = state.casp?.map((item) => {
            const result = { ...item };

            if (result.id === action.meta.arg.originalArgs.caspId) {
              let nextCaspCurrencies: CASPCurrency[] = [];

              if (result.currencies?.length) {
                nextCaspCurrencies = ([] as CASPCurrency[]).concat(
                  result.currencies,
                );
              }

              action?.payload?.data?.forEach((currency) => {
                nextCaspCurrencies.push(currency);
              });

              result.currencies = nextCaspCurrencies;
            }

            return result;
          });
        },
      )
      .addMatcher(
        transactionsTypesApi.endpoints.updateCaspCurrencies.matchFulfilled,
        (state, action: PayloadAction<{ data: CASPCurrency[] }>) => {
          const currentCaspId = action.payload.data[0].caspId;

          state.casp = state.casp?.map((item) => {
            const result = { ...item };

            if (result.id === currentCaspId) {
              let nextCaspCurrencies: CASPCurrency[] = [];

              if (result.currencies?.length) {
                nextCaspCurrencies = ([] as CASPCurrency[]).concat(
                  result.currencies,
                );
              }

              const updatedCurrencies = nextCaspCurrencies?.map((currency) => {
                const currencyResult = { ...currency };

                const currentCaspCurrency = action.payload.data?.find(
                  (currentCurrency) =>
                    currentCurrency.currencyId === currencyResult.currencyId,
                );

                if (currentCaspCurrency) {
                  currencyResult.active = currentCaspCurrency.active;
                }

                return currencyResult;
              });

              result.currencies = updatedCurrencies;
            }

            return result;
          });
        },
      )
      .addMatcher(
        transactionsTypesApi.endpoints.createCasp.matchFulfilled,
        (state, action: PayloadAction<{ data: CASP }>) => {
          let nextCasp: CASP[] = [];

          if (state.casp?.length) {
            nextCasp = ([] as CASP[]).concat(state.casp);
          }

          nextCasp.push(action.payload.data);

          state.casp = nextCasp;
        },
      )
      .addMatcher(
        transactionsTypesApi.endpoints.updateCasp.matchFulfilled,
        (state, action: PayloadAction<{ data: CASP }>) => {
          state.casp = state.casp?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = action.payload.data;
            }

            return result;
          });
        },
      )
      .addMatcher(
        transactionsTypesApi.endpoints.toggleTransactionTypeActive
          .matchFulfilled,
        (
          state,
          action: PayloadAction<{
            data: TransactionType;
          }>,
        ) => {
          // eslint-disable-next-line no-console
          console.log('toggleActive resp', action.payload.data);
        },
      );
  },
});

export const {
  onToggleParent,
  onToggleChildren,
  onUndoChange,
  clearCASPSidePanel,
} = transactionsTypesSlice.actions;

export default transactionsTypesSlice.reducer;
